/**
 * App scroll lives in `.index-scroll-main` (html/body are overflow:hidden).
 * Vue Router scrollBehavior only affects window — iOS Chrome/Safari often leave
 * the inner scroller wrong after the virtual keyboard closes (e.g. login → home).
 */

const EXTRA_SCROLL_ROOTS = ['#index_rightbar', '.index_rightbar', '.twod_warning'];

export function blurFocusedField() {
  const el = document.activeElement;
  if (!el || typeof el.blur !== 'function') return;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
    el.blur();
  }
}

function resetElementScroll(node) {
  if (!node) return;
  node.scrollLeft = 0;
  node.scrollTop = 0;
  if (typeof node.scrollTo === 'function') {
    try {
      node.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    } catch (_) {
      node.scrollTop = 0;
    }
  }
}

/** Clamp + nudge — fixes iOS/WebKit rubber-band / sub-pixel offset after keyboard (does not jump to top). */
function repairElementScroll(node) {
  if (!node) return;
  const max = Math.max(0, node.scrollHeight - node.clientHeight);
  let t = node.scrollTop;
  if (!Number.isFinite(t)) t = 0;
  t = Math.min(max, Math.max(0, t));
  node.scrollLeft = 0;
  node.scrollTop = t;
  if (typeof node.scrollTo === 'function') {
    try {
      node.scrollTo({ top: t, left: 0, behavior: 'auto' });
    } catch (_) {
      node.scrollTop = t;
    }
  }
  requestAnimationFrame(() => {
    const max2 = Math.max(0, node.scrollHeight - node.clientHeight);
    let t2 = Math.min(max2, Math.max(0, node.scrollTop));
    if (t2 <= 1) {
      node.scrollTop = 1;
      node.scrollTop = 0;
    } else {
      node.scrollTop = t2 - 1;
      node.scrollTop = t2;
    }
  });
}

export function repairScrollAfterKeyboard() {
  window.scrollTo(0, 0);
  if (document.documentElement) {
    document.documentElement.scrollLeft = 0;
    document.documentElement.scrollTop = 0;
  }
  if (document.body) {
    document.body.scrollLeft = 0;
    document.body.scrollTop = 0;
  }
  document.querySelectorAll('.index-scroll-main').forEach(repairElementScroll);
  EXTRA_SCROLL_ROOTS.forEach((sel) => {
    document.querySelectorAll(sel).forEach(repairElementScroll);
  });
}

export function resetAppScroll() {
  window.scrollTo(0, 0);
  if (document.documentElement) {
    document.documentElement.scrollLeft = 0;
    document.documentElement.scrollTop = 0;
  }
  if (document.body) {
    document.body.scrollLeft = 0;
    document.body.scrollTop = 0;
  }
  document.querySelectorAll('.index-scroll-main').forEach(resetElementScroll);
  EXTRA_SCROLL_ROOTS.forEach((sel) => {
    document.querySelectorAll(sel).forEach(resetElementScroll);
  });
}

/** Blur inputs and wait two frames so iOS can finish closing the keyboard before navigation. */
export function prepareForRouteChangeAfterKeyboard() {
  blurFocusedField();
  return new Promise((resolve) => {
    const done = () => {
      syncAppShellHeightFromVisualViewport();
      repairScrollAfterKeyboard();
      resolve();
    };
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => requestAnimationFrame(done));
    } else {
      setTimeout(done, 32);
    }
  });
}

let keyboardFixInstalled = false;

/**
 * iOS Chrome/Safari: `100dvh` / inner flex height can lag behind the real visible
 * area after the keyboard closes. Drive `.index-root` from visualViewport.height
 * so `.index-scroll-main` gets correct scrollHeight / max scroll.
 */
export function syncAppShellHeightFromVisualViewport() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const vv = window.visualViewport;
  const raw = vv && vv.height > 0 ? vv.height : window.innerHeight;
  const h = Math.max(1, Math.round(raw));
  document.documentElement.style.setProperty('--app-height', `${h}px`);
}

function layoutHeight() {
  return window.innerHeight || document.documentElement.clientHeight || 1;
}

/**
 * iOS Chrome/Safari: after the virtual keyboard closes, the inner scroller can stay
 * offset. We only repair when the keyboard actually dismisses (not while typing).
 * Uses visualViewport height with hysteresis; also repair when focus leaves fields.
 */
export function attachIosKeyboardScrollFix() {
  if (typeof window === 'undefined' || keyboardFixInstalled) {
    return () => {};
  }
  keyboardFixInstalled = true;

  let debounceId;
  let heightSyncId;
  let scrollRepairId;
  /** true while virtual keyboard is likely open (hysteresis avoids jitter while typing) */
  let keyboardLikelyOpen = false;
  const OPEN_RATIO = 0.72;
  const CLOSE_RATIO = 0.84;

  const scheduleHeightSync = () => {
    clearTimeout(heightSyncId);
    heightSyncId = setTimeout(() => {
      syncAppShellHeightFromVisualViewport();
    }, 0);
  };

  const scheduleScrollRepair = () => {
    clearTimeout(scrollRepairId);
    scrollRepairId = setTimeout(() => {
      repairScrollAfterKeyboard();
    }, 48);
  };

  const onVisualViewportResize = () => {
    scheduleHeightSync();
    const vv = window.visualViewport;
    if (!vv) return;
    const ratio = vv.height / layoutHeight();

    if (!keyboardLikelyOpen && ratio < OPEN_RATIO) {
      keyboardLikelyOpen = true;
      return;
    }
    if (keyboardLikelyOpen && ratio > CLOSE_RATIO) {
      keyboardLikelyOpen = false;
      clearTimeout(debounceId);
      debounceId = setTimeout(() => {
        syncAppShellHeightFromVisualViewport();
        repairScrollAfterKeyboard();
        setTimeout(() => {
          syncAppShellHeightFromVisualViewport();
          repairScrollAfterKeyboard();
        }, 280);
      }, 160);
    }
  };

  /** iOS often moves the layout/visual offset on `scroll`, not only `resize`. */
  const onVisualViewportScroll = () => {
    scheduleHeightSync();
    if (keyboardLikelyOpen) return;
    scheduleScrollRepair();
  };

  const onWindowResize = () => {
    scheduleHeightSync();
  };

  const onPageShow = (e) => {
    if (e.persisted) {
      syncAppShellHeightFromVisualViewport();
      repairScrollAfterKeyboard();
    }
  };

  const vv = window.visualViewport;
  syncAppShellHeightFromVisualViewport();
  if (vv && typeof vv.addEventListener === 'function') {
    vv.addEventListener('resize', onVisualViewportResize);
    vv.addEventListener('scroll', onVisualViewportScroll);
    onVisualViewportResize();
  }

  window.addEventListener('resize', onWindowResize);
  window.addEventListener('pageshow', onPageShow);

  const onFocusOut = (e) => {
    const t = e.target;
    if (!t || typeof t.tagName !== 'string') return;
    const tag = t.tagName;
    if (tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT') return;
    setTimeout(() => {
      const ae = document.activeElement;
      if (!ae || ae === document.body || ae === document.documentElement) {
        keyboardLikelyOpen = false;
        syncAppShellHeightFromVisualViewport();
        repairScrollAfterKeyboard();
      }
    }, 320);
  };
  document.addEventListener('focusout', onFocusOut, true);

  return () => {
    keyboardFixInstalled = false;
    clearTimeout(debounceId);
    clearTimeout(heightSyncId);
    clearTimeout(scrollRepairId);
    if (vv && typeof vv.removeEventListener === 'function') {
      vv.removeEventListener('resize', onVisualViewportResize);
      vv.removeEventListener('scroll', onVisualViewportScroll);
    }
    window.removeEventListener('resize', onWindowResize);
    window.removeEventListener('pageshow', onPageShow);
    document.removeEventListener('focusout', onFocusOut, true);
  };
}
